/**
 * Generato automaticamente.
 * framework-cmp-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  BEResponseDocumentiFascicoloWithToken,
  BEResponseFascicoliJwtResponse,
  BEResponseFascicolo,
  BEResponseListAllegatoDTO,
  FascicoloTokenRequest,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

export const postBackTipiIdSincronizzazioniProcedimenti = (
  idTipoProcedimento: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseFascicolo>(
    {
      url: `/framework-bo-procedimenti/tipi/${idTipoProcedimento}/sincronizzazioni-procedimenti`,
      method: 'post',
    },
    options,
  );
};

export const getPostBackTipiIdSincronizzazioniProcedimentiMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackTipiIdSincronizzazioniProcedimenti>>,
    TError,
    { idTipoProcedimento: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackTipiIdSincronizzazioniProcedimenti>>,
  TError,
  { idTipoProcedimento: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackTipiIdSincronizzazioniProcedimenti>>,
    { idTipoProcedimento: string }
  > = props => {
    const { idTipoProcedimento } = props ?? {};

    return postBackTipiIdSincronizzazioniProcedimenti(
      idTipoProcedimento,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackTipiIdSincronizzazioniProcedimentiMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postBackTipiIdSincronizzazioniProcedimenti>>
  >;

export type PostBackTipiIdSincronizzazioniProcedimentiMutationError =
  ErrorType<unknown>;

export const usePostBackTipiIdSincronizzazioniProcedimenti = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackTipiIdSincronizzazioniProcedimenti>>,
    TError,
    { idTipoProcedimento: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostBackTipiIdSincronizzazioniProcedimentiMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * API M2M per recuperare la lista di id del fascicolo richiesto e dei suoi sottofascicoli, e un token JWT da utilizzare per poterli consultare
 * @summary API M2M per recuperare la lista di id del fascicolo richiesto e dei suoi sottofascicoli, e un token JWT da utilizzare per poterli consultare
 */
export const postBackDocumentaleFascicoliIdToken = (
  idFascicolo: string,
  fascicoloTokenRequest: FascicoloTokenRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseFascicoliJwtResponse>(
    {
      url: `/framework-bo-procedimenti/documentale/fascicoli/${idFascicolo}/token`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: fascicoloTokenRequest,
    },
    options,
  );
};

export const getPostBackDocumentaleFascicoliIdTokenMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackDocumentaleFascicoliIdToken>>,
    TError,
    { idFascicolo: string; data: FascicoloTokenRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackDocumentaleFascicoliIdToken>>,
  TError,
  { idFascicolo: string; data: FascicoloTokenRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackDocumentaleFascicoliIdToken>>,
    { idFascicolo: string; data: FascicoloTokenRequest }
  > = props => {
    const { idFascicolo, data } = props ?? {};

    return postBackDocumentaleFascicoliIdToken(
      idFascicolo,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackDocumentaleFascicoliIdTokenMutationResult = NonNullable<
  Awaited<ReturnType<typeof postBackDocumentaleFascicoliIdToken>>
>;
export type PostBackDocumentaleFascicoliIdTokenMutationBody =
  FascicoloTokenRequest;
export type PostBackDocumentaleFascicoliIdTokenMutationError =
  ErrorType<unknown>;

/**
 * @summary API M2M per recuperare la lista di id del fascicolo richiesto e dei suoi sottofascicoli, e un token JWT da utilizzare per poterli consultare
 */
export const usePostBackDocumentaleFascicoliIdToken = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackDocumentaleFascicoliIdToken>>,
    TError,
    { idFascicolo: string; data: FascicoloTokenRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostBackDocumentaleFascicoliIdTokenMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * recuperato il procedimento per id, ritorna la lista di id dei suoi fascicoli e sottofascicoli e un token JWT da utilizzare per poterli consultare
 * @summary recupera la lista di id del fascicolo e sottofascicoli di un procedimento, e un token JWT da utilizzare per poterli consultare
 */
export const getBackProcedimentiIdFascicoli = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseFascicoliJwtResponse>(
    {
      url: `/framework-bo-procedimenti/procedimenti/${id}/fascicoli`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackProcedimentiIdFascicoliQueryKey = (id: string) =>
  [`/framework-bo-procedimenti/procedimenti/${id}/fascicoli`] as const;

export const getGetBackProcedimentiIdFascicoliQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackProcedimentiIdFascicoli>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackProcedimentiIdFascicoli>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackProcedimentiIdFascicoli>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackProcedimentiIdFascicoliQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackProcedimentiIdFascicoli>>
  > = ({ signal }) =>
    getBackProcedimentiIdFascicoli(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetBackProcedimentiIdFascicoliQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackProcedimentiIdFascicoli>>
>;
export type GetBackProcedimentiIdFascicoliQueryError = ErrorType<unknown>;

/**
 * @summary recupera la lista di id del fascicolo e sottofascicoli di un procedimento, e un token JWT da utilizzare per poterli consultare
 */
export const useGetBackProcedimentiIdFascicoli = <
  TData = Awaited<ReturnType<typeof getBackProcedimentiIdFascicoli>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackProcedimentiIdFascicoli>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackProcedimentiIdFascicoliQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * recuperato il procedimento per id, ritorna la lista di id dei suoi fascicoli e sottofascicoli e un token JWT da utilizzare per poterli consultare
 * @summary recupera il dettaglio di un fascicolo, se si dispone del token con i permessi per la consultazione
 */
export const getBackDocumentaleFascicoliId = (
  idFascicolo: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseFascicolo>(
    {
      url: `/framework-bo-procedimenti/documentale/fascicoli/${idFascicolo}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackDocumentaleFascicoliIdQueryKey = (idFascicolo: string) =>
  [`/framework-bo-procedimenti/documentale/fascicoli/${idFascicolo}`] as const;

export const getGetBackDocumentaleFascicoliIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackDocumentaleFascicoliId>>,
  TError = ErrorType<unknown>,
>(
  idFascicolo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackDocumentaleFascicoliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackDocumentaleFascicoliId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackDocumentaleFascicoliIdQueryKey(idFascicolo);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackDocumentaleFascicoliId>>
  > = ({ signal }) =>
    getBackDocumentaleFascicoliId(idFascicolo, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idFascicolo, ...queryOptions };
};

export type GetBackDocumentaleFascicoliIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackDocumentaleFascicoliId>>
>;
export type GetBackDocumentaleFascicoliIdQueryError = ErrorType<unknown>;

/**
 * @summary recupera il dettaglio di un fascicolo, se si dispone del token con i permessi per la consultazione
 */
export const useGetBackDocumentaleFascicoliId = <
  TData = Awaited<ReturnType<typeof getBackDocumentaleFascicoliId>>,
  TError = ErrorType<unknown>,
>(
  idFascicolo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackDocumentaleFascicoliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackDocumentaleFascicoliIdQueryOptions(
    idFascicolo,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * recupera l'elenco dei documenti di un fascicolo, se si dispone del token con i permessi per la consultazione del fascicolo
 * @summary recupera l'elenco dei documenti di un fascicolo, se si dispone del token con i permessi per la consultazione del fascicolo
 */
export const getBackDocumentaleFascicoliIdDocumenti = (
  idFascicolo: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseDocumentiFascicoloWithToken>(
    {
      url: `/framework-bo-procedimenti/documentale/fascicoli/${idFascicolo}/documenti`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackDocumentaleFascicoliIdDocumentiQueryKey = (
  idFascicolo: string,
) =>
  [
    `/framework-bo-procedimenti/documentale/fascicoli/${idFascicolo}/documenti`,
  ] as const;

export const getGetBackDocumentaleFascicoliIdDocumentiQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumenti>>,
  TError = ErrorType<unknown>,
>(
  idFascicolo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumenti>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackDocumentaleFascicoliIdDocumentiQueryKey(idFascicolo);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumenti>>
  > = ({ signal }) =>
    getBackDocumentaleFascicoliIdDocumenti(idFascicolo, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idFascicolo, ...queryOptions };
};

export type GetBackDocumentaleFascicoliIdDocumentiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumenti>>
>;
export type GetBackDocumentaleFascicoliIdDocumentiQueryError =
  ErrorType<unknown>;

/**
 * @summary recupera l'elenco dei documenti di un fascicolo, se si dispone del token con i permessi per la consultazione del fascicolo
 */
export const useGetBackDocumentaleFascicoliIdDocumenti = <
  TData = Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumenti>>,
  TError = ErrorType<unknown>,
>(
  idFascicolo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackDocumentaleFascicoliIdDocumentiQueryOptions(
    idFascicolo,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * recupera l'elenco degli allegati di un documento, se si dispone del token con i permessi per la consultazione del documento
 * @summary recupera l'elenco degli allegati di un documento, se si dispone del token con i permessi per la consultazione del documento
 */
export const getBackDocumentaleFascicoliIdDocumentiId = (
  idFascicolo: string,
  idDocumento: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseListAllegatoDTO>(
    {
      url: `/framework-bo-procedimenti/documentale/fascicoli/${idFascicolo}/documenti/${idDocumento}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackDocumentaleFascicoliIdDocumentiIdQueryKey = (
  idFascicolo: string,
  idDocumento: string,
) =>
  [
    `/framework-bo-procedimenti/documentale/fascicoli/${idFascicolo}/documenti/${idDocumento}`,
  ] as const;

export const getGetBackDocumentaleFascicoliIdDocumentiIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumentiId>>,
  TError = ErrorType<unknown>,
>(
  idFascicolo: string,
  idDocumento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumentiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumentiId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackDocumentaleFascicoliIdDocumentiIdQueryKey(
      idFascicolo,
      idDocumento,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumentiId>>
  > = ({ signal }) =>
    getBackDocumentaleFascicoliIdDocumentiId(
      idFascicolo,
      idDocumento,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(idFascicolo && idDocumento),
    ...queryOptions,
  };
};

export type GetBackDocumentaleFascicoliIdDocumentiIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumentiId>>
>;
export type GetBackDocumentaleFascicoliIdDocumentiIdQueryError =
  ErrorType<unknown>;

/**
 * @summary recupera l'elenco degli allegati di un documento, se si dispone del token con i permessi per la consultazione del documento
 */
export const useGetBackDocumentaleFascicoliIdDocumentiId = <
  TData = Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumentiId>>,
  TError = ErrorType<unknown>,
>(
  idFascicolo: string,
  idDocumento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumentiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackDocumentaleFascicoliIdDocumentiIdQueryOptions(
    idFascicolo,
    idDocumento,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * scarica il contenuto di un documento, se si dispone del token con i permessi richiesti
 * @summary scarica il contenuto di un documento, se si dispone del token con i permessi richiesti
 */
export const getBackDocumentaleFascicoliIdDocumentiIdDownload = (
  idFascicolo: string,
  idDocumento: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-bo-procedimenti/documentale/fascicoli/${idFascicolo}/documenti/${idDocumento}/download`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackDocumentaleFascicoliIdDocumentiIdDownloadQueryKey = (
  idFascicolo: string,
  idDocumento: string,
) =>
  [
    `/framework-bo-procedimenti/documentale/fascicoli/${idFascicolo}/documenti/${idDocumento}/download`,
  ] as const;

export const getGetBackDocumentaleFascicoliIdDocumentiIdDownloadQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getBackDocumentaleFascicoliIdDocumentiIdDownload>
  >,
  TError = ErrorType<unknown>,
>(
  idFascicolo: string,
  idDocumento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getBackDocumentaleFascicoliIdDocumentiIdDownload>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumentiIdDownload>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackDocumentaleFascicoliIdDocumentiIdDownloadQueryKey(
      idFascicolo,
      idDocumento,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumentiIdDownload>>
  > = ({ signal }) =>
    getBackDocumentaleFascicoliIdDocumentiIdDownload(
      idFascicolo,
      idDocumento,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(idFascicolo && idDocumento),
    ...queryOptions,
  };
};

export type GetBackDocumentaleFascicoliIdDocumentiIdDownloadQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getBackDocumentaleFascicoliIdDocumentiIdDownload>>
  >;
export type GetBackDocumentaleFascicoliIdDocumentiIdDownloadQueryError =
  ErrorType<unknown>;

/**
 * @summary scarica il contenuto di un documento, se si dispone del token con i permessi richiesti
 */
export const useGetBackDocumentaleFascicoliIdDocumentiIdDownload = <
  TData = Awaited<
    ReturnType<typeof getBackDocumentaleFascicoliIdDocumentiIdDownload>
  >,
  TError = ErrorType<unknown>,
>(
  idFascicolo: string,
  idDocumento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getBackDocumentaleFascicoliIdDocumentiIdDownload>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetBackDocumentaleFascicoliIdDocumentiIdDownloadQueryOptions(
      idFascicolo,
      idDocumento,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * scarica il contenuto di un allegato appartenente ad un documento principale, se si dispone del token con i permessi richiesti per accedere al documento
 * @summary scarica il contenuto di un allegato appartenente ad un documento principale, se si dispone del token con i permessi richiesti per accedere al documento
 */
export const getBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownload = (
  idFascicolo: string,
  idDocumento: string,
  idAllegato: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-bo-procedimenti/documentale/fascicoli/${idFascicolo}/documenti/${idDocumento}/allegati/${idAllegato}/download`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownloadQueryKey =
  (idFascicolo: string, idDocumento: string, idAllegato: string) =>
    [
      `/framework-bo-procedimenti/documentale/fascicoli/${idFascicolo}/documenti/${idDocumento}/allegati/${idAllegato}/download`,
    ] as const;

export const getGetBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownloadQueryOptions =
  <
    TData = Awaited<
      ReturnType<
        typeof getBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownload
      >
    >,
    TError = ErrorType<unknown>,
  >(
    idFascicolo: string,
    idDocumento: string,
    idAllegato: string,
    options?: {
      query?: UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownload
          >
        >,
        TError,
        TData
      >;
      request?: SecondParameter<typeof apiClientRequest>;
    },
  ): UseQueryOptions<
    Awaited<
      ReturnType<
        typeof getBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownload
      >
    >,
    TError,
    TData
  > & { queryKey: QueryKey } => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownloadQueryKey(
        idFascicolo,
        idDocumento,
        idAllegato,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof getBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownload
        >
      >
    > = ({ signal }) =>
      getBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownload(
        idFascicolo,
        idDocumento,
        idAllegato,
        requestOptions,
        signal,
      );

    return {
      queryKey,
      queryFn,
      enabled: !!(idFascicolo && idDocumento && idAllegato),
      ...queryOptions,
    };
  };

export type GetBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownloadQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof getBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownload
      >
    >
  >;
export type GetBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownloadQueryError =
  ErrorType<unknown>;

/**
 * @summary scarica il contenuto di un allegato appartenente ad un documento principale, se si dispone del token con i permessi richiesti per accedere al documento
 */
export const useGetBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownload = <
  TData = Awaited<
    ReturnType<
      typeof getBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownload
    >
  >,
  TError = ErrorType<unknown>,
>(
  idFascicolo: string,
  idDocumento: string,
  idAllegato: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<
          typeof getBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownload
        >
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetBackDocumentaleFascicoliIdDocumentiIdAllegatiIdDownloadQueryOptions(
      idFascicolo,
      idDocumento,
      idAllegato,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
